<template>
  <main v-if="post" class="post pt-[68px] pb-[150px] md:pt-14 md:pb-[168px]">
    <section class="post-section">
      <div class="container mx-auto max-w-5xl px-4">
        <h1 class="landing_h1 post-header text-center">
          {{ post.title }}
        </h1>
        <p
          class="post-description mt-10 mb-8 text-center text-sm text-[#666] md:mb-14 md:text-lg"
        >
          <Translate
            translate-key="blog.posted_on_by"
            :translate-options="{ posted_date: post.posted }"
          />
          <span class="mx-1">{{ post.author_name }}</span>
          <template v-if="post.category">
            <Translate class="mr-1" translate-key="blog.in" />
            <NuxtLink
              :to="localePath(`/blog/?categories=${post.category.id}`)"
              class="color-success underline"
            >
              {{ post.category.name }}
            </NuxtLink>
          </template>
        </p>
        <BlogPageInner
          :html-text="post.html_text"
          :top-image="getAbsoluteUrl(post.img_main)"
          :top-image-alt="post.img_main_alt_text"
        />
        <div class="posts-pagination mt-16 flex justify-between gap-2">
          <div
            v-if="post.prev_post"
            class="post-item flex w-1/2 flex-col md:flex-row md:items-center md:shadow-[0px_0px_40px_rgba(0,0,0,0.08)]"
          >
            <Button
              type="button"
              class="border-none rounded-none w-full h-full max-w-10 bg-[#4282c5] p-2.5 text-white hover:bg-[#316295]"
              :title="`To ${post.prev_post.title}`"
              @click="toPost(post.prev_post.slug)"
            >
              <ChevronLeft class="size-5 shrink-0" />
            </Button>
            <NuxtImg
              class="hidden h-[150px] w-[150px] object-cover md:block"
              :src="getAbsoluteUrl(post.prev_post.img_main)"
              :alt="post.prev_post.img_main_alt_text"
              width="150"
              height="150"
              format="webp"
              :placeholder="getBlogPlaceholderForKey(getAbsoluteUrl(post.prev_post.img_main))"
            />
            <div class="post-info my-4 md:mx-4 md:my-0 md:block md:w-full">
              <p class="text-xs uppercase text-gray-600">
                <Translate translate-key="blog.previous_article" />
              </p>
              <h3
                class="post-title m-0 text-lg font-semibold md:text-[1.3125rem]"
              >
                {{ post.prev_post.title }}
              </h3>
            </div>
          </div>
          <div
            v-if="post.next_post"
            class="post-item flex w-1/2 flex-col md:flex-row md:items-center md:shadow-[0px_0px_40px_rgba(0,0,0,0.08)]"
          >
            <div
              class="post-info next my-4 text-right md:mx-4 md:my-0 md:block md:w-full"
            >
              <p class="text-xs uppercase text-gray-600">
                <Translate translate-key="blog.next_article" />
              </p>
              <h3
                class="post-title m-0 text-lg font-semibold md:text-[1.3125rem]"
              >
                {{ post.next_post.title }}
              </h3>
            </div>
            <NuxtImg
              class="hidden h-[150px] w-[150px] object-cover md:block"
              :src="getAbsoluteUrl(post.next_post.img_main)"
              :alt="post.next_post.img_main_alt_text"
              width="150"
              height="150"
              format="webp"
              :placeholder="getBlogPlaceholderForKey(getAbsoluteUrl(post.next_post.img_main))"
            />
            <Button
              type="button"
              class="border-none rounded-none w-full h-full max-w-10 bg-[#4282c5] p-2.5 text-white hover:bg-[#316295]"
              :title="`To ${post.next_post.title}`"
              @click="toPost(post.next_post.slug)"
            >
              <ChevronRight class="size-5 shrink-0" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  </main>
  <div v-else class="px-4 py-12 text-center">
    <p>Post not found.</p>
    <NuxtLink :to="localePath('/blog')" class="text-teal-600 underline">
      Back to blog
    </NuxtLink>
  </div>
</template>

<script setup lang="ts">
import { ChevronLeft, ChevronRight } from "lucide-vue-next";
import Translate from "~/shared/components/i18n/Translate/Translate.vue";
import BlogPageInner from "~/slices/blog/components/BlogPageInner.vue";
import { useAsyncBlogPost } from "~/slices/blog/composables/queries";
import { getBlogPlaceholderForKey } from "~/shared/composables/useBlogPlaceholder";
import { useGetAbsoluteUrl } from "~/shared/composables/useGetAbsoluteUrl";
import { Button } from "~/shared/components/ui/button";

definePageMeta({ layout: "public" });

const route = useRoute();
const router = useRouter();
const localePath = useLocalePath();
const { getAbsoluteUrl } = useGetAbsoluteUrl();

const slug = computed(() => String(route.params.slug));
const { data } = useAsyncBlogPost(slug);
const post = data;

watch(
  post,
  (p) => {
    if (p === null) setResponseStatus(404);
  },
  { immediate: true },
);

function toPost(nextSlug: string) {
  router.push(localePath(`/blog/${nextSlug}`));
}

useSeoMeta({
  title: () =>
    post.value ? post.value.meta_title || post.value.title : "Blog",
  description: () =>
    post.value
      ? post.value.meta_description ||
        post.value.description ||
        "No custom description"
      : undefined,
  ogTitle: () => post.value?.meta_title ?? post.value?.title,
  ogDescription: () => post.value?.meta_description,
  ogImage: () => (post.value ? getAbsoluteUrl(post.value.img_main) : undefined),
});
</script>
